import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"
import {
  TRIAL_ENDING_SOON_DAYS,
  TRIAL_NEW_DAYS,
} from "@/v2-backoffice-client/license/trials/license.trials.constants"
import type { TrialKpis } from "@/v2-backoffice-client/license/trials/license.trials.utils"

export type LicenseTrialKpisProps = ComponentProps<"section"> & {
  kpis: TrialKpis
}

const KPI_COUNT = 6

function share(part: number, total: number) {
  if (total === 0) return null
  return `${Math.round((part / total) * 100)} %`
}

// The weekly post's headline numbers as tiles: how many are trialing and
// on what, how many people that is and how many of them actually use it,
// and the three worklists — ending soon, overdue, new. Each tile's sub-line
// says what the number means, so the strip reads without the table.
function LicenseTrialKpis(props: LicenseTrialKpisProps) {
  const { kpis, className, ...sectionProps } = props

  const productLine =
    kpis.byProduct.length > 0
      ? kpis.byProduct
          .map((entry) => `${formatNumber(entry.count)} ${entry.product}`)
          .join(" · ")
      : null

  return (
    <section
      className={cn(usageKpiGridClass(KPI_COUNT), className)}
      {...sectionProps}
    >
      <UsageReportKpi
        label="On trial"
        value={
          <>
            {formatNumber(kpis.customerCount)}
            <span className="truncate text-sm font-normal text-muted-foreground">
              {kpis.customerCount === 1 ? "customer" : "customers"}
            </span>
          </>
        }
        sub={[
          `${formatNumber(kpis.licenseCount)} ${kpis.licenseCount === 1 ? "trial license" : "trial licenses"}`,
          productLine,
        ]}
      />

      <UsageReportKpi
        label="People on trial"
        value={formatNumber(kpis.peopleCount)}
        sub={[
          <>
            <span className="font-medium text-foreground">
              {formatNumber(kpis.activePeople7d)}
            </span>{" "}
            active the last 7 days
            {share(kpis.activePeople7d, kpis.peopleCount) && (
              <> ({share(kpis.activePeople7d, kpis.peopleCount)})</>
            )}
          </>,
          `${formatNumber(kpis.activePeople28d)} active the last 28 days`,
        ]}
      />

      <UsageReportKpi
        label="Not using it"
        tone={kpis.silentPeople > 0 ? "warning" : "default"}
        value={
          <>
            {formatNumber(kpis.silentPeople)}
            <span className="truncate text-sm font-normal text-muted-foreground">
              {kpis.silentPeople === 1 ? "person" : "people"}
            </span>
          </>
        }
        sub={[
          "no tracked activity the last 28 days",
          kpis.silentCustomers > 0
            ? `${formatNumber(kpis.silentCustomers)} ${kpis.silentCustomers === 1 ? "customer" : "customers"} with nobody using it`
            : "every trialing customer has someone using it",
        ]}
      />

      <UsageReportKpi
        label="Ending soon"
        tone={kpis.endingSoon > 0 ? "warning" : "default"}
        value={formatNumber(kpis.endingSoon)}
        sub={`end within ${TRIAL_ENDING_SOON_DAYS} days — convert or end`}
      />

      <UsageReportKpi
        label="Overdue"
        tone={kpis.overdue > 0 ? "warning" : "default"}
        value={formatNumber(kpis.overdue)}
        sub="past the end date and still on trial"
      />

      <UsageReportKpi
        label="New this week"
        value={formatNumber(kpis.newThisWeek)}
        sub={`started in the last ${TRIAL_NEW_DAYS} days`}
      />
    </section>
  )
}

export default LicenseTrialKpis
